"use client"

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { api } from "../../services/Axios"
import { useAuth } from "../../services/AuthContext";
import debounce from "lodash.debounce"
import {
  FaBuilding,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
} from "react-icons/fa"
import PageHeader from "../ui/management/PageHeader"
import StatsCard from "../ui/management/StatsCard"
import Pagination from "../ui/management/Pagination"
import Loader from "../utils/loader/Loader"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import ReusableModal from "../utils/modals/ReusableModal"

// Definición de la interfaz para una sucursal
export interface Branch {
  _id: string
  nombre: string
  direccion?: string
  ciudad?: string
  telefono?: string
}

function ManageSucursales() {
  const { userData } = useAuth();

  // Estados para el listado y controles generales
  const [branches, setBranches] = useState<Branch[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Estados para modales (confirmación y mensajes)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string>("")
  const [modalAction, setModalAction] = useState<(() => void) | null>(null)
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
  const [messageContent, setMessageContent] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error" | "info" | "warning">("success")
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);

  // Estados para el modo de edición / solo lectura y la sucursal seleccionada
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [isReadOnlyMode, setIsReadOnlyMode] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null)

  // Estados para los campos del formulario de sucursal
  const [nombre, setNombre] = useState<string>("")
  const [direccion, setDireccion] = useState<string>("")
  const [ciudad, setCiudad] = useState<string>("")
  const [telefono, setTelefono] = useState<string>("")

  // Estados para mensajes de error en los campos
  const [nombreError, setNombreError] = useState<string>("")
  const [direccionError, setDireccionError] = useState<string>("")
  const [ciudadError, setCiudadError] = useState<string>("")

  // Estados para paginación
  const branchesPerPage = 6
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalBranches, setTotalBranches] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)

  useEffect(() => {
    if (userData?.organization?._id) {
      api.get("/api/subscriptions").then((res) => {
        setSubscriptionPlan(res.data?.subscription?.plan || null);
      });
    }
  }, [userData]);

  const sucursalesPorPlan: Record<string, number> = {
    basico: 1,
    estandar: 3,
    premium: 10,
  };
  
  const normalizarPlan = (plan: string): string =>
    plan.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/plan\s+/i, "").trim();
  
  const puedeAgregarSucursal = subscriptionPlan
    ? branches.length < (sucursalesPorPlan[normalizarPlan(subscriptionPlan)] ?? 0)
    : false;  
  
  // Función para obtener sucursales desde la API con filtros y paginación
  const fetchBranches = useCallback(
    (page: number, search: string) => {
      setIsLoading(true)
      api
        .get("/api/sucursales", {
          params: {
            page,
            limit: branchesPerPage,
            searchTerm: search || undefined,
          },
        })
        .then((response) => {
          const { sucursales, totalPages, totalSucursales } = response.data
          setBranches(sucursales)
          setTotalPages(totalPages)
          setTotalBranches(totalSucursales)
          setCurrentPage(page)
        })
        .catch((error) => console.error("Error al obtener sucursales:", error))
        .finally(() => setIsLoading(false))
    },
    [branchesPerPage],
  )

  useEffect(() => {
    fetchBranches(currentPage, searchTerm)
  }, [fetchBranches, currentPage, searchTerm])

  // Debounce para la búsqueda
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value)
        setCurrentPage(1)
      }, 500),
    [],
  )
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setCurrentPage(1)
    if (searchInputRef.current) {
      searchInputRef.current.value = ""
    }
    fetchBranches(1, "")
  }

  // Limpia los campos del formulario
  const clearFields = () => {
    setNombre("")
    setDireccion("")
    setCiudad("")
    setTelefono("")
  }

  // Limpia los errores de validación
  const clearErrors = () => {
    setNombreError("")
    setDireccionError("")
    setCiudadError("")
  }

  // Abre el modal de confirmación
  const openConfirmationModal = (message: string, action?: () => void) => {
    setModalMessage(message)
    setModalAction(() => action || closeModal)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalAction(null)
    setModalMessage("")
  }

  // Manejo de la acción de agregar sucursal
  const handleAddBranch = () => {
    setIsEditMode(false)
    clearFields()
    clearErrors()
    setIsModalOpen(true)
  }

  // Estados para mostrar el modal de creación/edición
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  
  // Acción para ver en modo solo lectura
  const handleViewBranch = (branch: Branch) => {
    setSelectedBranch(branch)
    setNombre(branch.nombre)
    setDireccion(branch.direccion || "")
    setCiudad(branch.ciudad || "")
    setTelefono(branch.telefono || "")
    setIsEditMode(false)
    setIsReadOnlyMode(true)
    clearErrors()
    setIsModalOpen(true)
  }

  // Acción para editar
  const handleEditBranch = (branch: Branch) => {
    setSelectedBranch(branch)
    setNombre(branch.nombre)
    setDireccion(branch.direccion || "")
    setCiudad(branch.ciudad || "")
    setTelefono(branch.telefono || "")
    clearErrors()
    setIsEditMode(true)
    setIsModalOpen(true)
  }

  // Función para guardar (agregar o editar) la sucursal
  const handleSaveBranch = () => {
    let hasError = false

    if (!nombre.trim()) {
      setNombreError("Nombre es obligatorio")
      hasError = true
    } else {
      setNombreError("")
    }

    if (!direccion.trim()) {
      setDireccionError("Dirección es obligatoria")
      hasError = true
    } else {
      setDireccionError("")
    }

    if (!ciudad.trim()) {
      setCiudadError("Ciudad es obligatoria")
      hasError = true
    } else {
      setCiudadError("")
    }

    if (hasError) return

    const saveAction = () => {
      if (isSaving) return; // 🔒 Previene múltiples ejecuciones
      setIsSaving(true)
      const branchData = { nombre, direccion, ciudad, telefono }

      const action = isEditMode && selectedBranch
        ? api.put(`/api/sucursales/${selectedBranch._id}`, branchData)
        : api.post("/api/sucursales/add", branchData)

      action
        .then(() => {
          setMessageContent(
            isEditMode
              ? "Sucursal actualizada exitosamente."
              : "Sucursal agregada exitosamente.",
          )
          setMessageType("success")
          setShowMessageModal(true)
          setIsModalOpen(false)
          fetchBranches(currentPage, searchTerm)
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.msg || "Error al guardar la sucursal."
          setMessageContent(errorMsg)
          setMessageType("error")
          setShowMessageModal(true)
        })
        .finally(() => {
          setIsSaving(false)
          closeModal()
        })
    }

    openConfirmationModal(
      isEditMode
        ? "¿Desea guardar los cambios en esta sucursal?"
        : "¿Desea agregar esta nueva sucursal?",
      saveAction,
    )
  }

  // Función para eliminar una sucursal
  const handleDeleteBranch = (branchId: string) => {
    openConfirmationModal("¿Está seguro de que desea eliminar esta sucursal?", () => {
      api
        .delete(`/api/sucursales/${branchId}`)
        .then(() => {
          setMessageContent("Sucursal eliminada exitosamente.")
          setMessageType("success")
          setShowMessageModal(true)
          fetchBranches(currentPage, searchTerm)
        })
        .catch(() => {
          setMessageContent("Error al eliminar la sucursal.")
          setMessageType("error")
          setShowMessageModal(true)
        })
        .finally(() => closeModal())
    })
  }

  const handleCloseMessageModal = () => {
    setShowMessageModal(false)
    closeModal()
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {isLoading && <Loader />}
      <ConfirmationModal
        isOpen={modalOpen}
        title="Confirmación"
        message={modalMessage}
        onConfirm={modalAction || closeModal}
        onCancel={closeModal}
        isProcessing={isSaving}
      />
      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={handleCloseMessageModal}
      />

      {/* Encabezado de página */}
      <PageHeader
        title="Gestión de Sucursales"
        subtitle="Administra las sucursales de tu organización"
        icon={FaBuilding}
        actionLabel={
          !puedeAgregarSucursal
            ? "Límite de sucursales alcanzado"
            : "Agregar Sucursal"
        }
        onAction={puedeAgregarSucursal ? handleAddBranch : undefined}
        actionIcon={FaPlus}
      />

      {/* Filtros y Búsqueda */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Sucursales
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre o dirección"
            onChange={handleSearchChange}
            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
            ref={searchInputRef}
          />
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Total Sucursales"
          value={totalBranches.toString()}
          description="Cantidad de sucursales registradas"
          icon={FaBuilding}
          gradient={false}
        />
      </div>

      {/* Tabla de Sucursales */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {branches.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dirección
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ciudad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {branches.map((branch) => (
                  <tr key={branch._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-[#2d53da]/10 rounded-full flex items-center justify-center">
                          <FaBuilding className="text-[#2d53da]" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{branch.nombre}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{branch.direccion}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{branch.ciudad}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleViewBranch(branch)}
                          title="Ver sucursal"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="bg-yellow-100 text-yellow-600 hover:bg-yellow-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleEditBranch(branch)}
                          title="Editar sucursal"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleDeleteBranch(branch._id)}
                          title="Eliminar sucursal"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaBuilding className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron sucursales</h3>
            <p className="text-gray-500 mb-4">
              {puedeAgregarSucursal
                ? "Intenta con otra búsqueda o agrega una nueva sucursal"
                : "Su suscripción no permite agregar más sucursales"}
            </p>
            <button
              onClick={puedeAgregarSucursal ? handleAddBranch : undefined}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                puedeAgregarSucursal
                  ? "bg-[#2d53da] hover:bg-[#2d53da]/90 text-white"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
            >
              <FaPlus className="mr-2" />
              {puedeAgregarSucursal ? "Agregar Sucursal" : "Límite alcanzado"}
            </button>
          </div>
        )}
      </div>

      {/* Paginación */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Modal para agregar/editar o ver sucursal */}
      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setIsReadOnlyMode(false)
        }}
        title={isReadOnlyMode ? "Ver Sucursal" : isEditMode ? "Editar Sucursal" : "Agregar Sucursal"}
        fields={[
          {
            label: "Nombre",
            value: nombre,
            onChange: setNombre,
            required: true,
            error: nombreError,
            readOnly: isReadOnlyMode,
          },
          {
            label: "Dirección",
            value: direccion,
            onChange: setDireccion,
            required: true,
            error: direccionError,
            readOnly: isReadOnlyMode,
          },
          {
            label: "Ciudad",
            value: ciudad,
            onChange: setCiudad,
            required: true,
            error: ciudadError,
            readOnly: isReadOnlyMode,
          },
          {
            label: "Teléfono",
            value: telefono,
            onChange: setTelefono,
            readOnly: isReadOnlyMode,
          },
        ]}
        onSave={handleSaveBranch}
        saveButtonText={isEditMode ? "Guardar Cambios" : "Agregar Sucursal"}
        readOnlyMode={isReadOnlyMode}
      />
    </div>
  )
}

export default ManageSucursales
